
import { ref, update, push } from 'firebase/database';
import { getDb } from './firebase';
import { dataAccess } from './dataAccess';
import { 
    Department, StaffMember, Shift, Schedule, ScheduleEntry,
    PickupLocation, ReservationSource, ShuttleReservation, 
    ParkingReservation, TimeOffRequest, AdminUser, 
    AttendanceRecord, Notification, DayOfWeek, ClosedPeriod, NightAuditReport,
    DailyNightAuditReport, WorkloadFactors,
    Memo, MemoCategory, MemoTopic, MemoTag,
    TaskCategory, TaskTemplate, TaskInstance,
    MaintenanceRequest, AccessRecord, AttendanceRequest,
    AttendanceArchive, PayrollPeriod
} from '../types';

// Specific data operations using the new Data Access Layer
export const database = {
    // Core Data
    saveScheduleData: async (data: any) => {
        const db = getDb();
        const updates: any = {};
        
        if (data.departments) updates['scheduleData/departments'] = data.departments;
        if (data.shifts) updates['scheduleData/shifts'] = data.shifts;
        if (data.pickupLocations) updates['scheduleData/pickupLocations'] = data.pickupLocations;
        if (data.reservationSources) updates['scheduleData/reservationSources'] = data.reservationSources;
        if (data.staffList) updates['scheduleData/staffList'] = data.staffList;
        if (data.schedule) updates['scheduleData/schedule'] = data.schedule;
        
        if (data.shuttleReservations) updates['shuttleReservations'] = data.shuttleReservations;
        if (data.parkingReservations) updates['parkingReservations'] = data.parkingReservations;
        if (data.archivedParkingReservations) updates['archivedParkingReservations'] = data.archivedParkingReservations;
        
        if (data.weekStartDay !== undefined) updates['scheduleData/weekStartDay'] = data.weekStartDay;
        if (data.propertyName !== undefined) updates['scheduleData/propertyName'] = data.propertyName;
        if (data.parkingCapacity !== undefined) updates['scheduleData/parkingCapacity'] = data.parkingCapacity;
        if (data.workloadFactors !== undefined) updates['scheduleData/workloadFactors'] = data.workloadFactors;

        await update(ref(db), updates);
    },

    // Individual updates
    updateSettings: async (settings: { weekStartDay?: DayOfWeek, propertyName?: string, parkingCapacity?: number, workloadFactors?: WorkloadFactors }) => {
        await dataAccess.saveSettings('scheduleData', settings);
    },

    // Users (Merged Admins & Staff)
    getAdmin: async (uid: string) => {
        return await dataAccess.getUser(uid) as AdminUser | null;
    },
    getUserByPhone: async (phone: string) => {
        return await dataAccess.getUserByPhone(phone) as AdminUser | null;
    },
    saveAdmin: async (admin: AdminUser) => {
        await dataAccess.createUser(admin.uid, admin);
    },
    updateAdmin: async (uid: string, data: Partial<AdminUser>) => {
        await dataAccess.updateUser(uid, data);
    },
    updateStaffMember: async (staffId: string, data: Partial<StaffMember>) => {
        await dataAccess.updateStaffMember(staffId, data);
    },
    updateScheduleEntry: async (staffId: string, date: string, data: Partial<ScheduleEntry>) => {
        await dataAccess.updateScheduleEntry(staffId, date, data);
    },
    deleteAdmin: async (uid: string) => {
        // We use status: 'suspended' for soft delete in production
        await dataAccess.updateUser(uid, { status: 'suspended' });
    },

    // Time Off Requests
    createTimeOffRequest: async (request: Omit<TimeOffRequest, 'id' | 'userId' | 'status' | 'createdAt' | 'staffName'>) => {
        const data = { ...request, userId: request.employeeId };
        // @ts-ignore - staffName is added by the service or expected to be in request if needed
        await dataAccess.createTimeOffRequest(data);
    },
    saveTimeOffRequest: async (request: TimeOffRequest) => {
        await dataAccess.createTimeOffRequest(request);
    },
    updateTimeOffRequest: async (requestId: string, data: Partial<TimeOffRequest>) => {
        await dataAccess.updateTimeOffRequest(requestId, data);
    },
    deleteTimeOffRequest: async (requestId: string) => {
        await dataAccess.updateTimeOffStatus(requestId, 'deleted');
    },

    // Attendance
    saveAttendanceRecord: async (record: Omit<AttendanceRecord, 'id'> | AttendanceRecord) => {
        const data = { 
            ...record, 
            userId: (record as any).employeeId || (record as any).staffId || (record as any).userId 
        };
        await dataAccess.recordAttendance(data);
    },

    // Closed Periods
    closePeriod: async (data: ClosedPeriod) => {
        await dataAccess.closePeriod(data);
    },
    getClosedPeriod: async (periodId: string) => {
        return await dataAccess.getClosedPeriod(periodId);
    },

    // Night Audit
    saveNightAuditReport: async (report: Partial<NightAuditReport>) => {
        return await dataAccess.saveNightAuditReport(report);
    },
    saveActiveNightAudit: async (audit: Partial<NightAuditReport>) => {
        await dataAccess.saveActiveNightAudit(audit);
    },
    deleteActiveNightAudit: async (auditId: string) => {
        await dataAccess.deleteActiveNightAudit(auditId);
    },

    // Daily Night Audit Reports
    saveDailyNightAuditReport: async (report: Partial<DailyNightAuditReport>) => {
        return await dataAccess.saveDailyNightAuditReport(report);
    },
    deleteDailyNightAuditReport: async (id: string) => {
        await dataAccess.deleteDailyNightAuditReport(id);
    },

    // Notifications
    saveNotification: async (notification: Notification) => {
        await dataAccess.sendNotification(notification.userId, notification);
    },
    updateNotification: async (id: string, data: Partial<Notification>) => {
        if (data.read) {
            await dataAccess.markNotificationRead(id);
        }
    },
    deleteNotification: async (id: string) => {
        await dataAccess.deleteNotification(id);
    },
    clearUserNotifications: async (userId: string) => {
        // This would require a query to find all notifications for a user
        // For now, we'll just implement it as a placeholder or add a specific method in dataAccess
        console.warn("clearUserNotifications not fully implemented in new schema");
    },

    // Memos
    createMemo: async (memo: Omit<Memo, 'id' | 'createdAt' | 'updatedAt'>) => {
        return await dataAccess.createMemo(memo);
    },
    updateMemo: async (memoId: string, data: Partial<Memo>, oldMemo?: Memo) => {
        await dataAccess.updateMemo(memoId, data, oldMemo);
    },
    deleteMemo: async (memoId: string, memo: Memo) => {
        await dataAccess.deleteMemo(memoId, memo);
    },
    createMemoCategory: async (name: string, color: string) => {
        return await dataAccess.createMemoCategory(name, color);
    },
    createMemoTopic: async (name: string, categoryId: string) => {
        return await dataAccess.createMemoTopic(name, categoryId);
    },
    createMemoTag: async (name: string) => {
        return await dataAccess.createMemoTag(name);
    },
    deleteMemoCategory: async (id: string) => {
        await dataAccess.deleteMemoCategory(id);
    },
    deleteMemoTopic: async (id: string) => {
        await dataAccess.deleteMemoTopic(id);
    },
    deleteMemoTag: async (id: string) => {
        await dataAccess.deleteMemoTag(id);
    },
    updateWeeklyMetric: async (date: string, field: string, value: number) => {
        await dataAccess.updateWeeklyMetric(date, field, value);
    },

    // Tasks
    createTaskCategory: async (category: Partial<TaskCategory>) => {
        return await dataAccess.createTaskCategory(category);
    },
    updateTaskCategory: async (id: string, data: Partial<TaskCategory>) => {
        await dataAccess.updateTaskCategory(id, data);
    },
    deleteTaskCategory: async (id: string) => {
        await dataAccess.deleteTaskCategory(id);
    },
    createTaskTemplate: async (template: Partial<TaskTemplate>) => {
        return await dataAccess.createTaskTemplate(template);
    },
    updateTaskTemplate: async (id: string, data: Partial<TaskTemplate>) => {
        await dataAccess.updateTaskTemplate(id, data);
    },
    deleteTaskTemplate: async (id: string) => {
        await dataAccess.deleteTaskTemplate(id);
    },
    createTaskInstance: async (instance: Partial<TaskInstance>) => {
        return await dataAccess.createTaskInstance(instance);
    },
    updateTaskInstance: async (id: string, data: Partial<TaskInstance>) => {
        await dataAccess.updateTaskInstance(id, data);
    },
    deleteTaskInstance: async (id: string) => {
        await dataAccess.deleteTaskInstance(id);
    },

    // Maintenance
    createMaintenanceRequest: async (request: Partial<MaintenanceRequest>) => {
        return await dataAccess.createMaintenanceRequest(request);
    },
    updateMaintenanceRequest: async (id: string, data: Partial<MaintenanceRequest>) => {
        await dataAccess.updateMaintenanceRequest(id, data);
    },
    deleteMaintenanceRequest: async (id: string) => {
        await dataAccess.deleteMaintenanceRequest(id);
    },

    // Access Manager
    createAccessRecord: async (data: Partial<AccessRecord>) => {
        return await dataAccess.createAccessRecord(data);
    },
    updateAccessRecord: async (id: string, data: Partial<AccessRecord>) => {
        await dataAccess.updateAccessRecord(id, data);
    },
    deleteAccessRecord: async (id: string) => {
        await dataAccess.deleteAccessRecord(id);
    },

    // Attendance Requests
    createAttendanceRequest: async (request: Partial<AttendanceRequest>) => {
        return await dataAccess.createAttendanceRequest(request);
    },
    updateAttendanceRequest: async (id: string, data: Partial<AttendanceRequest>) => {
        await dataAccess.updateAttendanceRequest(id, data);
    },
    archiveAttendanceRequests: async (archives: Omit<AttendanceArchive, 'id'>[], requestIdsToDelete: string[]) => {
        const db = getDb();
        const updates: any = {};
        
        archives.forEach(archive => {
            const archiveId = push(ref(db, 'attendanceArchives')).key;
            if (archiveId) {
                updates[`attendanceArchives/${archiveId}`] = {
                    ...archive,
                    id: archiveId
                };
            }
        });

        requestIdsToDelete.forEach(id => {
            updates[`attendanceRequests/${id}`] = null;
        });
        
        if (Object.keys(updates).length > 0) {
            await update(ref(db), updates);
        }
    },
    savePayrollPeriod: async (period: PayrollPeriod) => {
        const db = getDb();
        await update(ref(db, `payrollPeriods/${period.id}`), period);
    }
};

export const subscribeToPath = dataAccess.subscribe;
